import { safeStringify } from "@moment/api-collab";

import { type CellID } from "~/models/cell";
import { isEs5Identifier } from "~/utils/format/isEs5Identifier";

import { type TypeTagInfo, typeInfoFor } from "./TypeTagInfo";

/**
 * A fully qualified path to a field in a cell. For example, for an object:
 *
 *     v myCell = {
 *     >   a: { b: "c" }
 *       }
 *
 * We might have a path like `myCell.a.b === "c"`. This would be represented as:
 *
 *     [
 *       { type: "Cell", cellName: "myCell", ... },
 *       { type: "ObjectField", key: "a" ... },
 *       { type: "ObjectField", key: "b" ... },
 *     ]
 */
export type ValuePath = [ValuePathRoot, ...ValuePathComponent[]];

export type ValuePathSlice = (ValuePathComponent | ValuePathRoot)[];

/**
 * The root of a path. All paths are rooted in a named cell.
 */
export interface ValuePathRoot {
	type: "Cell";
	cellName: string;
	cellId: CellID;
	value: unknown;
	typeInfo: TypeTagInfo;
}

export function valuePathRoot(cellName: string, cellId: CellID, value: unknown): ValuePathRoot {
	const typeInfo = typeInfoFor(value);
	return { type: "Cell", cellName, cellId, value, typeInfo };
}

/**
 * A single field or element access in a path. For example, `myCell["a"].b.get("c")` contains an
 * `ArrayElement` component (for `["a"]`), an `ObjectField` component (for `.b`) and a `MapField`
 * component (for `.get("c")`).
 */
export type ValuePathComponent =
	| { type: "ObjectField"; key: string | symbol; value: unknown; typeInfo: TypeTagInfo }
	| { type: "ArrayElement"; key: number | string | symbol; value: unknown; typeInfo: TypeTagInfo }
	| { type: "MapField"; key: string; value: unknown; typeInfo: TypeTagInfo }
	| { type: "SetField"; index: number; value: unknown; typeInfo: TypeTagInfo };

export function arrayElement(key: number | string | symbol, value: unknown) {
	return { type: "ArrayElement", key, value, typeInfo: typeInfoFor(value) } as const;
}

export function objectField(key: string | symbol, value: unknown) {
	return { type: "ObjectField", key, value, typeInfo: typeInfoFor(value) } as const;
}

export function setField(index: number, value: unknown) {
	return { type: "SetField", index, value, typeInfo: typeInfoFor(value) } as const;
}

export function mapField(key: string, value: unknown) {
	return { type: "MapField", key, value, typeInfo: typeInfoFor(value) } as const;
}

export function concatPath<TPath = ValuePath | ValuePathSlice>(
	path: TPath,
	...components: ValuePathComponent[] | ValuePathSlice
): TPath {
	return [...(path as Array<unknown>), ...components] as TPath;
}

export function joinPath(path: ValuePath | ValuePathSlice) {
	if (path.length === 0) {
		return "";
	}

	const joining = stringifyComponent(path[0]);

	// Iterate from 1 because the specific path operator we use depends on what kind of field it is.
	// For example, `a.get("whatever")` for `Map`.
	for (let i = 1; i < path.length; i++) {
		const curr = path[i];
		joining.push(...stringifyComponent(curr));
	}

	return joining.join("");
}

const stringifyComponent = (curr: ValuePathRoot | ValuePathComponent | undefined) => {
	switch (curr?.type) {
		case "Cell":
			return [curr.cellName];
		case "ObjectField":
			if (typeof curr.key === "string" && isEs5Identifier(curr.key)) {
				return [".", curr.key];
			} else {
				return ["[", safeStringify(curr.key), "]"];
			}

		case "MapField":
			return [`.get(${safeStringify(curr.key)})`];

		case "SetField":
			throw new Error("Set can't be part of a path");

		case "ArrayElement":
			if (typeof curr.key === "string" && isEs5Identifier(curr.key)) {
				return [".", curr.key];
			} else {
				return ["[", safeStringify(curr.key), "]"];
			}

		default:
			throw new Error("not implemented");
	}
};

export function splitPathOnPrefix(
	path: ValuePath,
	prefix: ValuePath
): [ValuePath, ValuePathSlice] | undefined {
	const isPrefix = joinPath(path).startsWith(joinPath(prefix));
	if (!isPrefix) {
		return undefined;
	}

	const suffix = path.slice(prefix.length);
	return [prefix, suffix];
}

export function splitPathFromQueryRoot(path: ValuePath): [ValuePath, ValuePathSlice] {
	let pathToQueryRoot = path;
	let pathFromShallowestQueryRoot: (ValuePathComponent | ValuePathRoot)[] = [];
	for (let i = path.length - 1; i >= 0; i--) {
		if (path[i]?.typeInfo.typeTag === "Arrayish") {
			pathToQueryRoot = path.slice(0, i + 1) as ValuePath;
			pathFromShallowestQueryRoot = path.slice(i + 1);
			break;
		}
	}

	return [pathToQueryRoot, pathFromShallowestQueryRoot];
}
